import {useRef, useState} from 'react';
import {useViewportEffect} from 'react-viewport-utils';

export const useIsScrollingUp = ({
  bottomLimit,
  triggerThreshold = 10,
}: {
  bottomLimit?: number;
  /**
   * The threshold in pixels to consider a scroll event significant.
   */
  triggerThreshold?: number;
} = {}) => {
  const [isVisible, setIsVisible] = useState(false);

  const recentScrollPositions = useRef<number[]>([0, 0, 0]);

  const currentStateRef = useRef(false);

  useViewportEffect(
    ({scroll, dimensions}) => {
      const dimensionsHeight = bottomLimit ?? dimensions.height;

      if (scroll.y < dimensionsHeight) {
        if (currentStateRef.current) {
          setIsVisible(false);
          currentStateRef.current = false;
        }
        return;
      }

      recentScrollPositions.current = [
        scroll.y,
        recentScrollPositions.current[0],
        recentScrollPositions.current[1],
      ];

      const latest = recentScrollPositions.current[0] || 0;
      const oldest = recentScrollPositions.current[2] || 0;
      const scrollDelta = latest - oldest;

      if (scrollDelta < -triggerThreshold && !currentStateRef.current) {
        setIsVisible(true);
        currentStateRef.current = true;
      } else if (scrollDelta > triggerThreshold && currentStateRef.current) {
        const scrollDownLimit = Math.max(
          dimensionsHeight,
          dimensions.documentHeight - dimensionsHeight,
        );

        const shouldStayVisible = scroll.y > scrollDownLimit;

        if (!shouldStayVisible) {
          setIsVisible(false);
          currentStateRef.current = false;
        }
      }
    },
    {
      priority: 'low',
    },
    [bottomLimit],
  );

  return isVisible;
};
